// Heartbeat video kiểu mới: FE chỉ báo các ô 15s đã đi qua, server đóng dấu
// bằng giờ server và giới hạn nhịp theo thời gian thật (xem uniapp-backend
// utils/video-mapa.util.js). Logic nằm ở đây (không trong component) để test
// được bằng fake timers.
export const MAX_INDICES_PER_HEARTBEAT = 10;

export function bucketIndexAt(currentTime, intervalTime) {
  const t = Number(currentTime);
  if (!Number.isFinite(t) || t < 0) return null;
  return Math.floor(t / intervalTime);
}

// Ô đã đi qua mà server còn để 0 — server giới hạn nhịp nên có ô bị nhận
// chậm, phải báo lại ở tick sau. Mảng server rỗng = lần đầu, chưa biết độ
// dài nên báo hết.
export function pendingVideoIndices(touched, mapa, max = MAX_INDICES_PER_HEARTBEAT) {
  const hasServerMapa = Array.isArray(mapa) && mapa.length > 0;
  return [...touched]
    .filter((i) => Number.isInteger(i) && i >= 0)
    .filter((i) => !hasServerMapa || (i < mapa.length && !(Number(mapa[i]) > 0)))
    .sort((a, b) => a - b)
    .slice(0, max);
}

/**
 * Bộ điều khiển heartbeat cho 1 video (1 viewId).
 *
 * - `beat()` mỗi 15s khi đang phát; bỏ lượt nếu < `minGapMs` từ lượt trước
 *   (route giới hạn 1 request/2s — tránh ăn 429).
 * - `onPause()`: còn ô chờ thì gửi lại mỗi `flushDelayMs`, tối đa
 *   `flushMaxAttempts` lần — không có bước này ô cuối video không bao giờ
 *   được gửi (player tự dừng khi hết video).
 * - `dispose()` (rời trang): gửi bù 1 lượt + LƯU ô chờ vào storage theo
 *   viewId; lần mở lại video đó ô chờ được gửi tiếp. Server thường chưa nhận
 *   lượt gửi bù (chưa đủ 14,5s từ ô trước) nên phải lưu, không thì ô cuối mất.
 */
export function createVideoHeartbeat({
  send,
  getPosition,
  onResult = () => {},
  storage = null,
  storageKey = null,
  initialMapa = [],
  intervalSec = 15,
  minGapMs = 2500,
  flushDelayMs = 15000,
  flushMaxAttempts = 3,
  now = () => Date.now(),
  setTimeoutFn = (fn, ms) => setTimeout(fn, ms),
  clearTimeoutFn = (id) => clearTimeout(id),
}) {
  const touched = new Set();
  let mapa = Array.isArray(initialMapa) ? initialMapa : [];
  let lastSentAt = -Infinity;
  let lastTime = 0;
  let lastDuration = 0;
  let flushTimer = null;
  let disposed = false;

  if (storage && storageKey) {
    try {
      const saved = JSON.parse(storage.getItem(storageKey) || '[]');
      if (Array.isArray(saved)) saved.forEach((i) => { if (Number.isInteger(i) && i >= 0) touched.add(i); });
    } catch (_) { /* storage hỏng — bỏ qua */ }
  }

  const pending = () => pendingVideoIndices(touched, mapa);

  const persist = () => {
    if (!storage || !storageKey) return;
    try {
      const p = pending();
      if (p.length > 0) storage.setItem(storageKey, JSON.stringify(p));
      else storage.removeItem(storageKey);
    } catch (_) { /* hết quota / chế độ riêng tư */ }
  };

  const touch = (currentTime, duration) => {
    lastTime = Number(currentTime) || 0;
    if (Number(duration) > 0) lastDuration = Number(duration);
    const idx = bucketIndexAt(currentTime, intervalSec);
    if (idx !== null && !(Number(mapa[idx]) > 0)) touched.add(idx);
  };

  const beat = async ({ force = false } = {}) => {
    const pos = getPosition();
    if (!force && (!pos || pos.paused)) return pending().length;
    const duration = Math.floor((pos ? pos.duration : lastDuration) || 0);
    const currentTime = Math.floor((pos ? pos.currentTime : lastTime) || 0);
    if (duration <= 0) return pending().length;
    const indices = pending();
    if (force && indices.length === 0) return 0;
    if (now() - lastSentAt < minGapMs) return indices.length;
    lastSentAt = now();

    let result = null;
    try {
      result = await send({ currentTime, duration, indices });
    } catch (_) {
      result = null;
    }
    // `rejected` = server chưa đọc được gốc / đang bận — giữ ô chờ, thử lại sau.
    if (result && !result.rejected && Array.isArray(result.mapa)) {
      mapa = result.mapa;
      for (const i of [...touched]) {
        if (Number(mapa[i]) > 0 || i >= mapa.length) touched.delete(i);
      }
      if (!disposed) onResult(result);
    }
    persist();
    return pending().length;
  };

  const clearFlush = () => {
    if (flushTimer !== null) {
      clearTimeoutFn(flushTimer);
      flushTimer = null;
    }
  };

  const scheduleFlush = (attempt) => {
    clearFlush();
    if (attempt > flushMaxAttempts || disposed) return;
    flushTimer = setTimeoutFn(async () => {
      flushTimer = null;
      const remaining = await beat({ force: true });
      const pos = getPosition();
      if (remaining > 0 && (!pos || pos.paused)) scheduleFlush(attempt + 1);
    }, flushDelayMs);
  };

  return {
    touch,
    beat,
    setMapa: (m) => { if (Array.isArray(m)) mapa = m; },
    getMapa: () => mapa,
    pendingCount: () => pending().length,
    onPause: () => scheduleFlush(1),
    onPlay: () => clearFlush(),
    dispose: () => {
      clearFlush();
      persist();
      // Gửi bù lượt cuối; kết quả (nếu nhận) lại được persist() trong beat.
      const p = beat({ force: true });
      disposed = true;
      return p;
    },
  };
}
